import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Pencil, Trash2, Search, Users, Phone, Mail, Hash, FileText, Printer, UserCheck, UserX, TrendingUp } from "lucide-react";
import AccountMovements from "../components/AccountMovements";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";



const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movementsOpen, setMovementsOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [nextAccountNumber, setNextAccountNumber] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [formData, setFormData] = useState({
    name: "",
    account_number: "",
    phone: "",
    email: "",
    address: "",
    tax_number: "",
    notes: "",
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      toast.error("Müşteriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextAccountNumber = async () => {
    try {
      const response = await axios.get(`${API}/customers/next-account-number`);
      setNextAccountNumber(response.data.next_account_number);
      return response.data.next_account_number;
    } catch (error) {
      console.error("Failed to fetch next account number:", error);
      return "C1001";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("Müşteri adı zorunludur");
      return;
    }

    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData);
        toast.success("Müşteri güncellendi");
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success("Müşteri eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error("Failed to save customer:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("İşlem başarısız");
      }
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      account_number: customer.account_number || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
      tax_number: customer.tax_number || "",
      notes: customer.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success("Müşteri silindi");
      fetchCustomers();
    } catch (error) {
      console.error("Failed to delete customer:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleToggleStatus = async (customer) => {
    const action = customer.is_active !== false ? "pasif" : "aktif";
    if (!window.confirm(`Bu müşteriyi ${action} yapmak istediğinizden emin misiniz?`)) return;
    
    try {
      await axios.patch(`${API}/customers/${customer.id}/toggle-status`);
      toast.success(`Müşteri ${action} yapıldı`);
      fetchCustomers();
    } catch (error) {
      console.error("Failed to toggle customer status:", error);
      toast.error("İşlem başarısız");
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: "",
      account_number: "",
      phone: "",
      email: "",
      address: "",
      tax_number: "",
      notes: "",
    });
    setNextAccountNumber("");
  };

  const handleOpenDialog = async (isNew = true) => {
    if (isNew) {
      resetForm();
      const nextNum = await fetchNextAccountNumber();
      setFormData(prev => ({ ...prev, account_number: nextNum }));
    }
    setDialogOpen(true);
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.phone?.includes(searchTerm) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.account_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCustomers = filteredCustomers.filter(c => c.is_active !== false);
  const passiveCustomers = filteredCustomers.filter(c => c.is_active === false);
  const displayedCustomers = activeTab === "active" ? activeCustomers : passiveCustomers;

  // Toplam alacak hesaplama (pozitif bakiyeler = müşterilerin bize borcu)
  const totalReceivables = customers
    .filter(c => c.is_active !== false && c.balance > 0)
    .reduce((sum, c) => sum + c.balance, 0);
  
  const customersWithDebt = customers.filter(c => c.is_active !== false && c.balance > 0).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4 md:space-y-6 print:space-y-4" data-testid="customers-page">
      {/* Toplam Alacak Özet Kartı */}
      <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 border-0 shadow-lg">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm md:text-base font-medium">Toplam Alacak</p>
              <p className="text-white text-2xl md:text-3xl font-bold mt-1" data-testid="total-receivables">
                ₺{totalReceivables.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
              </p>
              <p className="text-emerald-100 text-xs md:text-sm mt-1">
                {customersWithDebt} müşteriden alacak var
              </p>
            </div>
            <div className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-white/20 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-white" />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 
            className="text-2xl md:text-3xl font-bold text-slate-900"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Müşteriler
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Müşteri kayıtlarını yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" 
                data-testid="add-customer-btn"
                onClick={() => handleOpenDialog(true)}
              >
                <Plus className="h-5 w-5 mr-2" />
                Yeni Müşteri
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Cari Hesap No</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="account_number"
                      value={formData.account_number}
                      onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                      placeholder="C1001"
                      className="pl-10 font-mono"
                      data-testid="customer-account-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Müşteri Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Firma veya kişi adı"
                    data-testid="customer-name-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="0532 xxx xx xx"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="ornek@mail.com"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tax_number">Vergi No</Label>
                <Input
                  id="tax_number"
                  value={formData.tax_number}
                  onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                  placeholder="Vergi numarası"
                  data-testid="customer-tax-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adres</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Adres bilgisi"
                  rows={2}
                  data-testid="customer-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notlar</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                  data-testid="customer-notes-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto">
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="customer-save-btn">
                  {editingCustomer ? "Güncelle" : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 md:pb-4 p-4 md:p-6">
          <div className="flex flex-col gap-4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="active" className="flex items-center gap-2">
                  <UserCheck className="h-4 w-4" />
                  Aktif ({activeCustomers.length})
                </TabsTrigger>
                <TabsTrigger value="passive" className="flex items-center gap-2">
                  <UserX className="h-4 w-4" />
                  Pasif ({passiveCustomers.length})
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Müşteri veya cari hesap no ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full"
                data-testid="customer-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {displayedCustomers.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {displayedCustomers.map((customer) => (
                  <div key={customer.id} className="p-4 space-y-3" data-testid={`customer-row-${customer.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-slate-900 truncate">{customer.name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            {customer.account_number && (
                              <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{customer.account_number}</span>
                            )}
                            {customer.tax_number && (
                              <span>VN: {customer.tax_number}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedCustomer(customer); setMovementsOpen(true); }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Hesap Hareketleri"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(customer)}
                          className={customer.is_active !== false ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                          title={customer.is_active !== false ? "Pasife Al" : "Aktife Al"}
                        >
                          {customer.is_active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(customer.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-sm">
                      {customer.phone && (
                        <a href={`tel:${customer.phone}`} className="flex items-center gap-1 text-slate-600">
                          <Phone className="h-4 w-4" />
                          {customer.phone}
                        </a>
                      )}
                      {customer.email && (
                        <a href={`mailto:${customer.email}`} className="flex items-center gap-1 text-slate-600 truncate">
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{customer.email}</span>
                        </a>
                      )}
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className="text-xs text-slate-500">Bakiye</span>
                      <span className={`font-semibold tabular-nums ${customer.balance > 0 ? "text-emerald-600" : customer.balance < 0 ? "text-red-600" : "text-slate-600"}`}>
                        ₺{customer.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Cari No</TableHead>
                      <TableHead className="font-semibold">Müşteri</TableHead>
                      <TableHead className="font-semibold">İletişim</TableHead>
                      <TableHead className="font-semibold">Vergi No</TableHead>
                      <TableHead className="font-semibold text-right">Bakiye</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedCustomers.map((customer) => (
                      <TableRow key={customer.id} data-testid={`customer-row-desktop-${customer.id}`}>
                        <TableCell>
                          <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                            {customer.account_number || "-"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{customer.name}</p>
                              {customer.address && (
                                <p className="text-sm text-slate-500 truncate max-w-[200px]">{customer.address}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {customer.phone && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Phone className="h-4 w-4" />
                                {customer.phone}
                              </div>
                            )}
                            {customer.email && (
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Mail className="h-4 w-4" />
                                {customer.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{customer.tax_number || "-"}</TableCell>
                        <TableCell className="text-right">
                          <span className={`font-semibold tabular-nums ${customer.balance > 0 ? "text-emerald-600" : customer.balance < 0 ? "text-red-600" : "text-slate-600"}`}>
                            ₺{customer.balance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setSelectedCustomer(customer); setMovementsOpen(true); }}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              title="Hesap Hareketleri"
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(customer)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(customer)}
                              className={customer.is_active !== false ? "text-orange-600 hover:text-orange-700 hover:bg-orange-50" : "text-green-600 hover:text-green-700 hover:bg-green-50"}
                              title={customer.is_active !== false ? "Pasife Al" : "Aktife Al"}
                            >
                              {customer.is_active !== false ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(customer.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {searchTerm 
                  ? "Arama sonucu bulunamadı" 
                  : activeTab === "active" 
                    ? "Henüz aktif müşteri bulunmuyor" 
                    : "Henüz pasif müşteri bulunmuyor"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <AccountMovements
        open={movementsOpen}
        onClose={() => setMovementsOpen(false)}
        accountType="customer"
        accountId={selectedCustomer?.id}
        accountName={selectedCustomer?.name}
      />
    </div>
  );
};

export default Customers;
